import React from 'react';
import { motion } from 'framer-motion';
import { 
  CircleStackIcon, 
  UserCircleIcon, 
  PhotoIcon, 
  UserGroupIcon,
  SparklesIcon,
  ArrowRightIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import { User } from '../../types';

interface ProfileChecklistProps {
  user: User;
  onAction?: (action: string) => void;
}

const ProfileChecklist: React.FC<ProfileChecklistProps> = ({ user, onAction }) => {
  const isProfilePicDone = !!user.profile_pic;
  const isScoreDone = (user.completion_score || 0) >= 60;
  const isCollabDone = (user.project_count || 0) > 0;
  const isPortfolioDone = (user.portfolio_items_count || 0) > 0;

  const tasks = [
    {
      id: 'score',
      label: 'Fill Up Profile Details',
      isDone: isScoreDone,
      value: `${user.completion_score || 0}%`,
      icon: CircleStackIcon,
      action: 'edit-profile'
    },
    {
      id: 'photo',
      label: 'Profile Picture',
      isDone: isProfilePicDone,
      icon: UserCircleIcon,
      action: 'upload-photo'
    },
    {
      id: 'portfolio',
      label: 'Add Portfolio Item',
      isDone: isPortfolioDone,
      icon: PhotoIcon,
      action: 'add-portfolio'
    },
    {
      id: 'collab',
      label: 'Create Project',
      isDone: isCollabDone,
      icon: UserGroupIcon,
      action: 'create-collaboration'
    }
  ];

  if (user.user_type === 'model') {
    tasks.push({
      id: 'model-specs',
      label: 'Set Model Specifications',
      isDone: !!user.model_profile,
      icon: SparklesIcon,
      action: 'edit-model-specs'
    });
  }

  const completedCount = tasks.filter(t => t.isDone).length;
  const progress = (completedCount / tasks.length) * 100;
  const hasReward = !!user.eligible_reward_id;

  // Hide checklist once all tasks are complete
  if (completedCount === tasks.length) {
    return null;
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
    >
      <div className={`px-4 py-3 ${hasReward ? 'bg-gradient-to-r from-teal-500 to-emerald-600' : 'bg-gray-900 dark:bg-gray-700'} text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {hasReward ? <SparklesIcon className="w-4 h-4" /> : <CheckCircleIconSolid className="w-4 h-4 text-white/70" />}
            <h2 className="text-xs font-black uppercase tracking-widest">
              {hasReward ? 'Reward Checklist' : 'Profile Completion'}
            </h2>
          </div>
          <span className="bg-white/20 px-2 py-0.5 rounded-full text-[10px] font-black">
            {completedCount}/{tasks.length}
          </span>
        </div>
        
        <div className="mt-2 relative h-1 bg-white/20 rounded-full overflow-hidden">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            className="absolute top-0 left-0 h-full bg-white shadow-[0_0_5px_rgba(255,255,255,0.5)]"
          />
        </div>
      </div>

      <div className="p-3 space-y-2">
        {tasks.map((task) => (
          <div 
            key={task.id}
            className="flex items-center justify-between group"
          >
            <div className="flex items-center space-x-3">
              <div className={`p-1.5 rounded-lg ${task.isDone ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-600' : 'bg-gray-50 dark:bg-gray-700 text-gray-400'}`}>
                <task.icon className="w-3.5 h-3.5" />
              </div>
              <span className={`text-[11px] font-bold uppercase tracking-tight ${task.isDone ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
                {task.label}
              </span>
            </div>
            
            {task.isDone ? (
              <CheckCircleIconSolid className="w-4 h-4 text-teal-500" />
            ) : (
              <button 
                onClick={() => onAction?.(task.action)}
                className="text-teal-600 hover:text-teal-700 transition-colors"
                title="Complete Task"
              >
                <ArrowRightIcon className="w-3.5 h-3.5 stroke-[3]" />
              </button>
            )}
          </div>
        ))}

        <div className="mt-4 pt-3 border-t border-gray-50 dark:border-gray-700">
           <div className="flex items-start space-x-2 text-[10px] text-gray-500 dark:text-gray-400 italic leading-tight">
              <InformationCircleIcon className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-500" />
              <p>Tip: Only completed profiles get visibility in search results and recommendations.</p>
           </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProfileChecklist;
